<script setup>
import { ref, onBeforeUnmount, watch, nextTick, computed } from 'vue'
import { createPopper } from '@popperjs/core'

const props = defineProps({
    text: { type: String, required: true },
    placement: { type: String, default: 'top' },
    offset: { type: Number, default: 8 },
    maxWidth: { type: Number, default: 260 },
})

const isVisible = ref(false)
const triggerRef = ref(null)
const tooltipRef = ref(null)

const tooltipId = computed(() => `tt-${Math.random().toString(36).slice(2, 9)}`)

let hideTimeout = null
let popperInstance = null

function show() {
    clearTimeout(hideTimeout)
    isVisible.value = true
}

function hide() {
    hideTimeout = setTimeout(() => {
        isVisible.value = false
    }, 80)
}

async function initPopper() {
    if (!triggerRef.value || !tooltipRef.value) return

    popperInstance?.destroy()
    popperInstance = createPopper(triggerRef.value, tooltipRef.value, {
        placement: props.placement,
        modifiers: [
            { name: 'offset', options: { offset: [0, props.offset] } },
            { name: 'flip', options: { fallbackPlacements: ['top', 'bottom'] } },
            { name: 'preventOverflow', options: { padding: 8 } },
        ],
    })
}

watch(isVisible, async (v) => {
    if (v) {
        await nextTick()
        await initPopper()
        popperInstance?.update()
    } else {
        popperInstance?.destroy()
        popperInstance = null
    }
})

onBeforeUnmount(() => {
    clearTimeout(hideTimeout)
    popperInstance?.destroy()
})
</script>

<template>
    <span
        ref="triggerRef"
        class="relative inline-flex"
        @mouseenter="show"
        @mouseleave="hide"
        @focusin="show"
        @focusout="hide"
        :aria-describedby="isVisible ? tooltipId : undefined"
    >
        <slot />
    </span>

    <Teleport to="body">
        <transition
            enter-active-class="transition-opacity duration-100"
            leave-active-class="transition-opacity duration-100"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isVisible"
                :id="tooltipId"
                ref="tooltipRef"
                role="tooltip"
                :style="{ maxWidth: `${maxWidth}px` }"
                class="z-[9999] px-2.5 py-2 text-[11px] leading-[1.35] rounded-lg border shadow-[0_6px_18px_rgba(0,0,0,0.12)] font-medium pointer-events-none
                       bg-white text-slate-900 border-slate-300
                       dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700
                       whitespace-normal break-words"
            >
                {{ text }}
            </div>
        </transition>
    </Teleport>
</template>
